// This only holds types for now, will add recource in next PR
import { type BasicUserWithPermissions } from './Model/User';
import Role from './Model/Role';
import ActivityType from './Model/ActivityType';
import CustomField, {
	ProductCustomField,
	EntityCustomField,
	CustomFieldWithStages,
	TicketCustomField,
	ProjectPlanCustomField
} from './Model/CustomField';
import OrderStage from './Model/OrderStage';
import ProductCategory from './Model/ProductCategory';
import Category, { CategoryType } from './Model/Category';
import UserDefinedObjectType from './Model/UserDefinedObjectType';
import { FilterConfig } from 'App/babel/filterConfigs/FilterConfig';
import { ComparisonTypeName } from 'Resources/ComparisonTypes';
import PriceList from './Model/PriceList';
import PaymentExtension from './Model/PaymentExtension';
import { Sort } from 'Resources/RequestBuilder';
import { ProjectPlanType } from './Model/ProjectPlan';
import ProjectPlanStatus from './Model/ProjectPlanStatus';
import { TicketStatus, TicketType } from './Model/Ticket';
import ProjectPlanStage from './Model/ProjectPlanStage';
import type ProjectBoard from './Model/ProjectBoard';
import ProjectPlanPriority from './Model/ProjectPlanPriority';
import { NoticePeriod, OrderOffset, OrderInterval, PeriodLength } from './Model/Agreement';
import type UserParams from './Model/UserParams';

import type { BuildFilters } from 'Resources/RequestBuilder';

export type AccountSelf = {
	id: number;
	name: string;
	email: string;
	version: string;
	features: {
		[key: string]: any;
	};
	client: {
		id: number;
		userId: number;
		name: string;
		numberOfLicenses: number;
		isTrial: boolean;
		contactEmail: string;
		killDate: null | string;
		migrated: 0 | 1;
		movingStarted: null | string;
		provisioningMaxContactTotal: number;
		country: string;
		delayedPayment: null | string;
		provisioningTrial: boolean;
		stillActive: boolean;
	};
	clients: {
		clientId: number;
		name: string;
		userId: number;
	}[];
	accountManager: {
		email: string;
		name: string;
		title: string;
		phone: string;
		cellPhone: string;
		sort?: number;
		/* eslint-disable camelcase */
		user_cellPhone?: null | string;
		user_phone?: null | string;
		user_title?: null | string;
		/* eslint-enable camelcase */
	};
	products: {
		[key: string]: boolean;
	};
	unreleasedFeatures: {
		[key: string]: boolean;
	};
	boughtAddons: {
		[key: string]: boolean;
	};
	versionData: {
		availableAddons: string[];
		includedAddons?: string[];
		features: string[];
		name: string;
		parent: string | null;
		products: { crm: boolean; ma: boolean };
		trialAddons: string[];
	};
	availableAddons: string[];
	lastOnline: null | string;
	lastOnlinePower: null | string;
	language: string;
};

export type CreateRight = 'ALL' | 'OWN' | 'NO' | 'YES';

type CreateRights = {
	Activity: CreateRight;
	Agreement: CreateRight;
	Appointment: CreateRight;
	Client: CreateRight;
	Contact: CreateRight;
	Document: CreateRight;
	Exchange: CreateRight;
	Opportunity: CreateRight;
	Order: CreateRight;
	Project: CreateRight;
	Report: CreateRight;
	SocialEvent: CreateRight;
	UserDefObj1: CreateRight;
	UserDefObj2: CreateRight;
	UserDefObj3: CreateRight;
	UserDefObj4: CreateRight;
};

export type Self = {
	id: number;
	name: string;
	email: string;
	administrator: boolean;
	regDate: string;
	userParams: UserParams;
	language: string;
	clients: Array<{ clientId: number }>;
	role: null | {
		id: number;
		name: string;
	};
	billingAdmin: boolean;
	teamLeader: boolean;
	export: boolean;
	createRights: CreateRights;
	userAddress: string | null;
	userState: string | null;
	userZipCode: string | null;
	userPhone: string | null;
	custom: EntityCustomField[];
	userTitle?: string | null;
	userCellPhone?: string | null;
	crm: boolean;
	support: boolean;
	service: boolean;
	supportAdmin: boolean;
	projectAdmin: boolean;
};

export type DocumentTemplate = {
	id: number;
	name: string;
	roles: {
		id: number;
		name: string;
		description: string;
	}[];
	type: string;
};

export type ListViewFilter = {
	filterName: string;
	value: any;
	comparisonType?: ComparisonTypeName;
	inactive?: boolean;
	field?: string | null;
	valueText?: string;
	forceType?: FilterConfig['type'];
};

type ListViewAndReportViewShared = {
	type: string;
	title: string;
	filters: ListViewFilter[];
	default: boolean;
	shared: boolean;
};

// Shared types between standard and user ListView
type ListViewShared = ListViewAndReportViewShared & {
	columns: string[];
	sorting: Sort[];
	groupBy?: string;
	limit: number;
	/** If true, this view is shown in the "Hidden lists" section" */
	hidden: boolean;
	/** If false, the view is not visible in the menu but can still be navigated to" */
	visible?: boolean;
	description: string;
	userlist: { id: string; title: string; isRole: boolean };
	roles: number[];
	users: number[];
	/** Places the view in a custom group */
	group?: string;
	tooltip?: string;
	/** If true, indicates this view should display a warning when containing items */
	warning?: boolean;
};

// Views created by upsales
export type StandardListView = ListViewShared & {
	id: string;
	private: false;
	editable: false;
};

// Views created by users
export type UserListView = ListViewShared & {
	id: number;
	private: boolean;
	editable: boolean;
	regBy: number;
	shared: boolean;
	builtFilters: null | BuildFilters;
	usedForProspectingMonitor: boolean;
};

type DateFilterValue = { start: Date | null; end: Date | null; preset: string };

export function isSalesboardColumnDateFilter(filter: any): filter is DateFilterValue {
	return !!filter.preset;
}

export type SalesboardViewColumn = {
	id?: number;
	sorting: { attribute: string; ascending: boolean; id: number; sortId: number }[];
	title: string;
	sortOrder: number;
	type: 'order' | 'opportunity' | 'appointment';
	image?: string;
	filters: ListViewFilter[];
	columnLimit?: number;
};

// SalesboardViews
type SalesboardListViewShared = ListViewAndReportViewShared & {
	type: 'salesboard';
	standard: boolean;
	roles: [];
	users: [];
	filters: ListViewFilter[];
	columns: SalesboardViewColumn[];
	isStandardView: false;
	uuid?: string | null;
	description?: string;
	columnLimit?: number | null;
	regDate?: string;
	modDate?: string;
	regBy?: number;
	sort?: string | null;
	shared?: boolean;
};

export function isSalesboardViewColumn(column: any): column is SalesboardViewColumn {
	return column.type !== 'report';
}

export type SalesboardStandardListView = SalesboardListViewShared & {
	id: string;
	private: false;
	editable: false;
	isStandardView: true;
};
export type SalesboardUserListView = SalesboardListViewShared & {
	id: number | 'standard1';
	private: boolean;
	editable: boolean;
	shared: boolean;
};

export type SalesboardListView = SalesboardUserListView | SalesboardStandardListView;

export type ListView = StandardListView | UserListView;

export function isStandardListView(listView: any): listView is StandardListView {
	return typeof listView.id == 'string' && listView.id.includes('standard');
}

export function isStandardSalesboardListView(listView: any): listView is SalesboardStandardListView {
	return typeof listView.id == 'string' && listView.id.includes('standard');
}

export function isSalesboardUserListView(listView: any): listView is SalesboardUserListView {
	return listView.shared;
}

export function isUserListView(listView: any): listView is UserListView {
	return listView.regBy !== undefined;
}

export function isSalesboardListView(listView?: any): listView is SalesboardListView {
	return listView?.type === 'salesboard';
}

export type ReportView = {
	type: string;
	filters: ListViewFilter[];
	columns: string[];
	default: boolean;
	description: string;
	editable: boolean;
	grouping: string;
	id: string;
	private: boolean;
	sorting: string[];
	tableGrouping: string | null;
	title: string;
	shared?: boolean;
	public?: boolean;
};

export type StandardFieldConfig = {
	name: string;
	tooltip: null | string;
	id: number;
	active: boolean;
	group: string;
	canHide: boolean;
	nameTag: string;
	field: string;
	required: boolean;
	type?: string;
	fieldNameTranslations?: {
		[key: string]: {
			value: string;
		};
	};
	selectOptions?: string | string[];
	disabled: boolean;
	editable: boolean;
	sortOrder: number;
	canMakeRequired: boolean;
	descTag?: string;
	icon?: {
		class: string;
		name?: string;
	};
	elevioId?: number;
	size?: number;
};

export type IntegrationConfig = {
	color: string;
	id: number;
	imageLink: null | string;
	inits: string[];
	name: string;
	userConfigurable: boolean;
	userOnly: boolean;
	capabilities?: {
		/* The scheme a dial link must use for a telephony app that cannot place the call itself
		   and takes the number from the OS instead. Read by VoiceService.dialProps. */
		useCallToLinks?: string;
	};
};

export type IntegrationInit = {
	public: boolean;
	alias: null | string;
	capabilities?: {
		typeahead?: {
			idField: string;
			columns: any;
			countryTabs: { enabled: boolean; showAll: boolean };
			pagination: { enabled: boolean };
			waitTime: number;
			hqBoButton?: boolean;
		};
		countries: string[];
		match: { upsales: 'dunsNo'; integration: string };
		pricing: { buy: { cost: number; endpoint: 'buy' } };
		logo: string;
		refresh?: boolean;
		onDemand?: boolean;
	};
	color: string;
	id: number;
	imageLink: null | string;
	name: string;
	userConfigurable: boolean;
	listenTo: string[];
	isAgentListener?: boolean;
	customerId?: number;
	event?: string;
};

export type UiElementConfig = {
	integrationId: number | string;
	name: string;
	type?: string;
	integrationName?: string;
};

export type UiElementAgentConfig = {
	type: string;
	name: string;
	data: any;
	integrationId: number;
};

type OnboardingStep = {
	id: number;
	title: string;
	shadowTitle: string;
	substepTitle: null | string;
	description: string;
	substepDescription: null | string;
	videoLink: string;
	videoThumbnail: string;
	articleTitle: string;
	articleLink: string;
	hasSubSteps: boolean;
	stepType: string;
	sortId: number;
	parentId: number;
	substeps?: OnboardingStep[];
};

export type Currency = {
	masterCurrency: boolean;
	iso: string;
	active: boolean;
	rate: number;
};

export type SalesModelType = 'sales' | 'cm' | 'rr';
export type SalesModelOptionType = 'cm' | 'sales' | 'arr' | 'mrr';
export type SalesModelOption3Type = 'onlyCM' | 'combinedWithCM' | 'onlyRR' | 'cmCombinedWithARR' | 'cmCombinedWithMRR';

export type OpportunityRiskSettings = {
	noNextStep: boolean;
	noAppointments: boolean;
	postponedAppointments: boolean;
	closingDateChanged: boolean;
	notAnsweringCalls: boolean;
	orderValue: boolean;
	orderValueChanged: boolean;
	noRecentContact: boolean;
	companySize: boolean;
};

export type MetadataParams = {
	teamAccountManager: boolean;
	defaultStageId: number;
	DefaultActivityTypeId: number;
	clientActivityRelation: string;
	clientOrderRelation: string;
	mailCredits: number;
	RelateAnySubaccount: boolean;
	soliditetIsActive: boolean;
	soliditetCredits: number;
	HideSoliditetToggle: boolean;
	callToType: string;
	callToTarget: string;
	AgreementEnabled: boolean;
	AgreementPrecreate: boolean;
	sessionTime: number;
	DiskSpaceForFilesLimit: number;
	UseDiscount: boolean;
	startPage: string;
	OrderedProductPriceDecimals: number;
	DnBCountryCodes: any;
	MultiCurrency: boolean;
	SmsAuthentication: boolean;
	telPrefix: string;
	LookerExploreAccess: number[];
	LookerIsActive: boolean;
	CurrencyAutoUpdate: boolean;
	DefaultAppointmentTypeId: number;
	ExtraSoliditetCredits: number;
	LastBisnodeResetDate: null;
	LastBisnodeSetDate: null;
	HasMigratedCustomFields: boolean;
	HasSamlLogin: boolean;
	UseContributionMargin: boolean;
	SalesboardSumContributionMargin: boolean;
	SyncJourneyStatus: boolean;
	HideReportCenter: boolean;
	SubscriptionDefaultInterval: OrderInterval;
	SubscriptionDefaultPeriod: PeriodLength;
	SubscriptionDefaultCreationTime: OrderOffset;
	LookerDashboardExeptionIds: string[];
	EnableAutoFillAppointmentsDescription: boolean;
	OrderProductsImprovedSearch: boolean;
	LookerTrialEnabled: boolean;
	EnableProductCategoryTabDefault: boolean;
	brokenFiscalYearEnabled: boolean;
	brokenFiscalYearOffset: number;
	SubscriptionDefaultStageId: number;
	SubscriptionLockedStages: number[];
	SubscriptionAdminCanEditLockedStage: boolean;
	SalesModel: SalesModelType;
	SalesModelOption: SalesModelOptionType;
	SalesModelOption2: string;
	SyncLogEmails: unknown[];
	SubscriptionInAdvanceOptions: OrderOffset[];
	ProspectingTrialCredits: string;
	ProspectingBulkCredit: boolean;
	SignalsAutoUpdate: boolean;
	CustomerRegion: 'SE' | 'GB' | 'NO';
	GroupARRChangesByUser: boolean;
	RemovedBouncesCurrentMonth: number;
	IncludeAPIKeysInReports: boolean;
	AISuggestionsEnabled: boolean;
	SalesModelOption3: SalesModelOption3Type;
	AutoAddProspectingData: boolean;
	DelayNewFeatures: boolean;
	DisableSegmentIO: boolean;
	DisableIntercom: boolean;
	SubscriptionNoticePeriod: NoticePeriod[];
	SubscriptionDefaultNoticePeriod: NoticePeriod;
	TicketPriority: boolean;
	EnableCreditOrders: boolean;
	AutoCreateSubscriptionPeriods: boolean;
	TicketMatching: boolean;
	TicketAutoCreateClientAndContact: boolean;
	TicketRulesApplyToManualTickets: boolean;
	TicketInternalOnPartialReply: boolean;
	UserDefinedObject: UserDefinedObjectType[];
	TicketSender: boolean;
	TicketSendDefaultStatus: string;
	AutomaticCurrencyRate: boolean;
	SubscriptionAddNotesToOrderCustomField: number;
	AutoFillAddressFromMaps: boolean;
	AppDirectoryAbleSetPremium: boolean;
	AppPublicPublisher: boolean;
	OpportunityRiskSettings: OpportunityRiskSettings;
	FormPasswordEnabled: boolean;
	AdjustPurchaseCostForOrderInterval: boolean;
	CurrencyRateChange: boolean;
	AdminSkipRequiredFields: boolean;
	ContactScraperRoleFilter?: string;
	TicketAutoSignature: boolean;
	TicketForwardSameClientAsExternal: boolean;
	DiscountFromEnteredPrice: boolean;
	'aiWorkspace.llmProvider': string;
	'aiWorkspace.llmModel': string;
	'aiWorkspace.llmModelFast': string;
};

export type Metadata = {
	customerCurrencies: Currency[];
	defaultCurrency: Currency;
	params: MetadataParams;
	/** Every published public release note, so the app can count the unseen ones. */
	publishedReleaseNoteIds: number[];
	/** Those published recently enough to announce unprompted. A subset of the above. */
	recentReleaseNoteIds: number[];
	credits: {
		email: number;
		sms?: number;
		bisnode: number;
	};
	map: { mail: boolean; mailActivated: boolean };
	user: { id: number; userCellPhone: string; userPhone: string; userTitle: string };
	role: {
		defaultCurrency: string;
		description: string;
		id: number;
		name: string;
		parentId: number;
		template: number;
	};
	activatedFeatures: {
		easyBooking: {
			bookingUrl: string;
			active: boolean;
			bookingPages: {
				title: string;
				id: number;
				bookingUrl: string;
				active: boolean;
				isShared: boolean;
			}[];
		};
	};
	standardFields: { [key: string]: { [key: string]: StandardFieldConfig } };
	requiredFields: { [key: string]: { [key: string]: boolean } };
	integrations: {
		userConfigurable: IntegrationConfig[];
		active: IntegrationConfig[];
		inits: {
			phone?: IntegrationInit[];
			erp?: IntegrationInit[];
			mail?: IntegrationInit[];
			esign?: IntegrationInit[];
			sms?: IntegrationInit[];
			webinar?: IntegrationInit[];
			fileStorage?: IntegrationInit[];
			calendar?: IntegrationInit[];
			job?: IntegrationInit[];
			chatNotifications?: IntegrationInit[];
			dataSource?: IntegrationInit[];
			// eslint-disable-next-line camelcase
			app_status_order?: IntegrationInit[];
			// eslint-disable-next-line camelcase
			after_order_edit?: IntegrationInit[];
		};
		uiElements: {
			editActivity?: { sidebar: UiElementConfig[] };
			editAppointment?: { sidebar: UiElementConfig[] };
			editOrder?: { sidebar: UiElementConfig[] };
			editSubscription?: { sidebar: UiElementConfig[] };
			editTicket?: { sidebar: UiElementConfig[] };
			account?: { [k in string]: UiElementConfig[] };
			contact?: { [k in string]: UiElementConfig[] };
			modals?: { name: string }[];
		};
	};
	agentLiveListeners: IntegrationInit[];
	agentUiElements: {
		editActivity?: { sidebar: UiElementAgentConfig[] };
		editAppointment?: { sidebar: UiElementAgentConfig[] };
		editOrder?: { sidebar: UiElementAgentConfig[] };
		editSubscription?: { sidebar: UiElementAgentConfig[] };
		editTicket?: { sidebar: UiElementAgentConfig[] };
		contact?: { [k in string]: UiElementAgentConfig[] };
	};
	licenses: number;
	supportLicenses: number;
	soliditetCredits: number;
	metaChannel: string;
	notificationChannel: string;
	notificationUserChannel: string;
	publicChannel: string;
	iOSInterest: string;
	publicUrlHash: string;
	mailEditorHash: string;
	iCalExternalUrlHash: string;
	esign: { active: boolean };
	mainApps: [];
	onboarding: {
		id: number;
		name: string;
		rolePath: string;
		regDate: string;
		modDate: string;
		steps: OnboardingStep[];
		onboardingCompletedId: null;
	}[];
	validFileExtensions: string;
	listOnboarding: {
		listIntro: {
			activity: boolean;
			agreement: boolean;
			appointment: boolean;
			client: boolean;
			contact: boolean;
			email: boolean;
			esign: boolean;
			flow: boolean;
			form: boolean;
			groupMail: boolean;
			lead: boolean;
			mailTemplate: boolean;
			opportunity: boolean;
			order: boolean;
			phoneCall: boolean;
			project: boolean;
			salesboard: boolean;
			segment: boolean;
			visit: boolean;
		};
		track: null;
	};
	userQuotaPeriods: {
		contributionMargin: string;
		oneOff: string;
		recurring: string;
		sales: string;
		// eslint-disable-next-line camelcase
		booked_appointment: string;
	};
};

export type ObjectReportViews = {
	own: ReportView[];
	public: ReportView[];
	shared?: ReportView[];
};

export type Brand = {
	id: number;
	name: string;
	logo?: string;
	active: boolean;
};

export type AllIWantData = {
	customerSelf: Self;
	self: AccountSelf;
	accessRights: { Agreement: boolean; SocialEvent: boolean };
	paymentExtensions?: PaymentExtension[];
	totals: {
		accounts: number;
		activeCallList: number;
		activeCampaigns: number;
		activeContacts: number;
		activeProducts: number;
		campaigns: number;
		contacts: number;
		products: number;
		oldActivities: number;
	};
	userMap: {
		[key: string]: BasicUserWithPermissions[];
	};
	roleMap: {
		[key: string]: Role[];
	};
	brands: Brand[];
	todoTypes: {
		PHONE_CALL: ActivityType;
		TODO: ActivityType;
	};
	customFields: {
		order: CustomFieldWithStages[];
		orderrow: CustomFieldWithStages[];
		opportunity: CustomFieldWithStages[];
		account: CustomField[];
		agreement: CustomField[];
		activity: CustomField[];
		todo: CustomField[];
		appointment: CustomField[];
		contact: CustomField[];
		product: ProductCustomField[];
		project: CustomField[];
		projectPlan: ProjectPlanCustomField[];
		lead: CustomField[];
		ticket: TicketCustomField[];
		user: CustomField[];
		userDefined1: CustomField[];
		userDefined2: CustomField[];
		userDefined3: CustomField[];
		userDefined4: CustomField[];
	};
	documentTemplates: {
		activity: DocumentTemplate[];
		agreement: DocumentTemplate[];
		client: DocumentTemplate[];
		object1: DocumentTemplate[];
		object2: DocumentTemplate[];
		object3: DocumentTemplate[];
		object4: DocumentTemplate[];
		order: DocumentTemplate[];
	};
	userDefinedCategoryTypes: {
		[key: string]: CategoryType[];
	};
	clientCategoryTypes: CategoryType[];
	contactCategoryTypes: CategoryType[];
	userDefinedCategories: {
		[key: string]: Category[];
	};
	listViews: {
		salesboard: SalesboardListView[];
		form: ListView[];
		page: ListView[];
		advancedSearchAccount: ListView[];
		advancedSearchContact: ListView[];
		advancedSearchActivity: ListView[];
		advancedSearchAppointment: ListView[];
		advancedSearchOpportunity: ListView[];
		advancedSearchOrder: ListView[];
		order: ListView[];
		agreement: ListView[];
		phoneCall: ListView[];
		visitor: ListView[];
		formSubmit: ListView[];
		mailCampaign: ListView[];
		account: ListView[];
		contact: ListView[];
		mail: ListView[];
		mailTemplate: ListView[];
		activity: ListView[];
		opportunity: ListView[];
		campaign: ListView[];
		callList: ListView[];
		leads2: ListView[];
		segment: ListView[];
		flow: ListView[];
		projectPlan: ListView[];
		ticket: ListView[];
		projectBoard: ProjectBoard[];
		projectMap: ProjectBoard[];
		[key: string]: (SalesboardListView | ListView | ProjectBoard)[];
	};
	metadata: Metadata;
	reportViews: {
		[key: string]: ReportView[] | ObjectReportViews;
	};
	prospectingTokenCost: {
		SE: number;
		FI: number;
		ES: number;
	};
	hasSharedViews: boolean;
	orderStages: OrderStage[];
	productCategories: ProductCategory[];
	clientCategories: Category[];
	contactCategories: Category[];
	activityTypes: ActivityType[];
	appointmentTypes: ActivityType[];
	acceptTerms: {
		show: boolean;
		accepted: boolean;
		termsId: number | null;
		termsLink: string;
		termsDesc: string;
	};
	hasFullReportAccess: boolean;
	priceLists: PriceList[];
	customerSupportForwardEmail: string;
	ticketTypes: TicketType[];
	projectPlanTypes: ProjectPlanType[];
	projectPlanStatuses: ProjectPlanStatus[];
	projectPlanStages: ProjectPlanStage[];
	projectPlanPriorities: ProjectPlanPriority[];
	ticketStatuses: TicketStatus[];
};
